import React, { useEffect, useRef } from 'react';

const COLORS = ['#ffd54a', '#ffffff', '#ff5d8f', '#4ade80', '#60a5fa', '#f97316'];

function readVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Lightweight canvas confetti. Fires a burst every time `fireKey` changes (and is truthy).
 * Fills its positioned parent; no dependencies.
 */
export default function ConfettiCanvas({ fireKey, disabled = false }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(0);

  // Keep canvas sized to its parent (device-pixel aware)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!fireKey || disabled) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const scale = Math.max(W, H) / 1000;
    const palette = [
      readVar('--primaryColor', '#086cbf'),
      readVar('--secondaryColor', '#b51913'),
      ...COLORS,
    ];

    const make = (x, y, angle, speed) => ({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      w: (6 + Math.random() * 8) * scale,
      h: (10 + Math.random() * 12) * scale,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.35,
      tilt: Math.random() * Math.PI * 2,
      vt: 0.08 + Math.random() * 0.12,
      color: palette[Math.floor(Math.random() * palette.length)],
      shape: Math.random() < 0.25 ? 'circle' : 'rect',
    });

    const burst = [];
    const n = 160;
    for (let i = 0; i < n; i++) {
      // Two cannons from the bottom corners
      const left = i % 2 === 0;
      const base = left ? -Math.PI / 3 : (-2 * Math.PI) / 3;
      const angle = base + (Math.random() - 0.5) * 0.6;
      const speed = (18 + Math.random() * 16) * scale;
      burst.push(make(left ? 0 : W, H, angle, speed));
    }
    for (let i = 0; i < 90; i++) {
      // Gentle rain from the top
      burst.push(
        make(
          Math.random() * W,
          -Math.random() * H * 0.3,
          Math.PI / 2,
          (2 + Math.random() * 3) * scale
        )
      );
    }
    particlesRef.current = particlesRef.current.concat(burst);

    const gravity = 0.45 * scale;
    const drag = 0.985;
    const step = () => {
      ctx.clearRect(0, 0, W, H);
      const alive = [];
      for (const p of particlesRef.current) {
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.vx += Math.sin(p.tilt) * 0.15 * scale;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.tilt += p.vt;
        if (p.y < H + 40 * scale) alive.push(p);
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.scale(1, Math.cos(p.tilt));
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      particlesRef.current = alive;
      if (alive.length > 0) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        ctx.clearRect(0, 0, W, H);
      }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [fireKey, disabled]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden='true'
      className='pointer-events-none absolute inset-0'
      style={{ zIndex: 30 }}
    />
  );
}
